import { useCallback, useState } from 'react'

import { useTranslation } from 'react-i18next'

import type { CivilDate } from '~/models/civilDate'
import {
  type DeleteErrorInfo,
  type MasterCrudRow,
  type ModalState,
} from '~/pages/AdminOcctoMaster/shell/types'
import { mapMasterGetError } from '~/pages/AdminOcctoMaster/tabs/_common/getError'
import { useCsvExportHandler } from '~/pages/AdminOcctoMaster/tabs/_common/hooks/useCsvExportHandler'
import { useExpiryFilter } from '~/pages/AdminOcctoMaster/tabs/_common/hooks/useExpiryFilter'
import type { CsvColumnDef } from '~/pages/AdminOcctoMaster/tabs/_common/models/csvExport'

type ExpiryOption<Row> = {
  getPeriod: (row: Row) => {
    validFrom: CivilDate
    validTo: CivilDate | undefined
  }
  /**
   * 「終了した{{subject}} N 件を非表示中」の subject に差し込む pre-resolved i18n 文言。
   * (BG / 事業者 / 計画提出者 / 発電所 / 需要調達計画 / 需要抑制計画 / 発電販売計画)
   */
  subject: string
}

type CsvOption<Row> = {
  columns: ReadonlyArray<CsvColumnDef<Row>>
  /** csvFileName に渡すタブ識別子 (例: 'bg', 'demand-procurement-plan')。 */
  fileBase: string
}

type Args<Row extends MasterCrudRow> = {
  /**
   * List RPC の生レスポンス (undefined = 未取得 or エラーで失敗)。
   * CSV エクスポート gate と errorLabel gate のためだけに使う。
   */
  data: unknown
  isLoading: boolean
  isError: boolean
  error: unknown
  /**
   * `page:adminOcctoMaster.<master>.serverError` (mapMasterGetError の prefix)。
   */
  errorKeyPrefix: string
  /**
   * 検索・エリア等のタブ固有フィルタを適用済みの行。expiry フィルタはこの hook が
   * 適用する (順序が逆だと hiddenCount 母集団がズレる、`useExpiryFilter` doc 参照)。
   */
  filteredRows: readonly Row[]
  csv: CsvOption<Row>
  /** expiry filter を無効化するタブ (取引先タブ) は undefined を渡す。 */
  expiry?: ExpiryOption<Row>
}

export type MasterCrudTab<Row> = {
  // modal
  modalState: ModalState<Row>
  openCreate: () => void
  openEdit: (row: Row) => void
  openDelete: (row: Row) => void
  /** モーダルを閉じ deleteError を同時にクリアする。 */
  closeModal: () => void
  // rows / expiry
  visibleRows: Row[]
  today: CivilDate
  includeExpired: boolean
  setIncludeExpired: (v: boolean) => void
  hiddenCount: number
  /**
   * ExpiredFilterToggle のラベルに出す終了した行の件数 (「終了した〜も表示 (N)」)。
   * トグルの状態に関わらず数える。expiry オプション未指定時は常に 0。
   */
  expiredCount: number
  /**
   * MasterDataTable の空状態文言 override。全件失効かつ hiddenCount > 0 のときのみ
   * 「終了した{{subject}} N 件を非表示中」を返す。それ以外は undefined (テーブル既定文言)。
   */
  emptyLabel: string | undefined
  /** List RPC 失敗時のテーブル本体のエラー文言。 */
  errorLabel: string | undefined
  // csv
  handleCsvExport: () => void
  /** false のとき MasterTableActionBar の CSV ダウンロードは disabled 化される (AC-05)。 */
  csvExportEnabled: boolean
  /**
   * CSV ダウンロードを押せない理由のツールチップ (一覧の読み込み中 / 読み込み失敗)。 一覧があれば undefined。
   * 読み込み中から操作バーを出すので、 未実装の「準備中」 と取り違えないよう理由を分ける。
   */
  csvExportDisabledReason: string | undefined
  /** 初回 loading (isLoading && data === undefined) 用の loading ロゴ表示 gate。 */
  showInitialLoading: boolean
  // delete error (useMasterInlineDelete が set する / dialog 側の onError で set する共通 slot)
  deleteError: DeleteErrorInfo | undefined
  setDeleteError: (err: DeleteErrorInfo | undefined) => void
}

const NO_EXPIRY_PERIOD = {
  validFrom: { year: 1970, month: 1, day: 1 } as CivilDate,
  validTo: undefined,
}
const noExpiryGetPeriod = (): {
  validFrom: CivilDate
  validTo: CivilDate | undefined
} => NO_EXPIRY_PERIOD

/**
 * OCCTO マスタ 8 タブ (BG / 事業者 / 発電所 / 計画提出者 / 発電販売計画 /
 * 需要調達計画 / 需要抑制計画 / 取引先) 共通の CRUD タブ shell hook。
 *
 * 各タブが 8 回写経していた modal state + expiry filter + labels + csv +
 * loading gate + deleteError slot を集約する。inline delete mutation を持つ
 * BgMaster / BusinessMaster / PlantMaster は `useMasterInlineDelete` を
 * 追加で呼んで handleDeleteConfirm を得る。dialog 側で delete を実行する
 * 他 5 タブは dialog の onError → setDeleteError を繋ぐだけでよい。
 */
export function useMasterCrudTab<Row extends MasterCrudRow>(
  args: Args<Row>,
): MasterCrudTab<Row> {
  const { t } = useTranslation()
  const {
    data,
    isLoading,
    isError,
    error,
    errorKeyPrefix,
    filteredRows,
    csv,
    expiry,
  } = args

  const [modalState, setModalState] = useState<ModalState<Row>>({
    kind: 'none',
  })
  const [deleteError, setDeleteError] = useState<DeleteErrorInfo | undefined>(
    undefined,
  )

  // hook を条件付きで呼ぶと react-hooks/rules-of-hooks 違反になるため、expiry
  // オプション不在時も useExpiryFilter は呼び、getPeriod だけ noop に差し替える
  // (常に有効期間内扱い → visibleRows = filteredRows / hiddenCount = 0)。
  // enabled=false で today の 1 分 interval を止め、下流テーブルの毎分再描画を
  // 避ける (取引先タブ regression 対応、PR #5383 review)。
  const expiryResult = useExpiryFilter<Row>(
    filteredRows,
    expiry?.getPeriod ?? noExpiryGetPeriod,
    expiry !== undefined,
  )

  const visibleRows = expiryResult.visibleRows
  const includeExpired = expiryResult.includeExpired
  const setIncludeExpired = expiryResult.setIncludeExpired
  const today = expiryResult.today
  const hiddenCount = expiry !== undefined ? expiryResult.hiddenCount : 0

  const allHiddenByExpiry = visibleRows.length === 0 && hiddenCount > 0
  const emptyLabel =
    expiry !== undefined && allHiddenByExpiry
      ? t('page:adminOcctoMaster.common.hiddenExpiredCount', {
          count: hiddenCount,
          subject: expiry.subject,
        })
      : undefined
  const errorLabel =
    isError && data === undefined
      ? mapMasterGetError(t, error, errorKeyPrefix)
      : undefined
  const expiredCount = expiry !== undefined ? expiryResult.expiredCount : 0

  const handleCsvExport = useCsvExportHandler(
    visibleRows,
    csv.columns,
    csv.fileBase,
  )
  const csvExportEnabled = data !== undefined
  const csvExportDisabledReason = csvExportEnabled
    ? undefined
    : isError
      ? t('common:masterTableActionBar.csvExportLoadFailed')
      : t('common:masterTableActionBar.csvExportLoading')

  const openCreate = useCallback(() => {
    setModalState({ kind: 'create' })
  }, [])
  const openEdit = useCallback((row: Row) => {
    setModalState({ kind: 'edit', row })
  }, [])
  const openDelete = useCallback((row: Row) => {
    setDeleteError(undefined)
    setModalState({ kind: 'delete', row })
  }, [])
  const closeModal = useCallback(() => {
    setModalState({ kind: 'none' })
    setDeleteError(undefined)
  }, [])

  const showInitialLoading = isLoading && data === undefined

  return {
    modalState,
    openCreate,
    openEdit,
    openDelete,
    closeModal,
    visibleRows,
    today,
    includeExpired,
    setIncludeExpired,
    hiddenCount,
    expiredCount,
    emptyLabel,
    errorLabel,
    handleCsvExport,
    csvExportEnabled,
    csvExportDisabledReason,
    showInitialLoading,
    deleteError,
    setDeleteError,
  }
}
